package dev.terrakok.mapitan.ui.components

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Popup
import androidx.compose.ui.window.PopupProperties
import dev.terrakok.mapitan.ui.Icons
import dev.terrakok.mapitan.domain.utils.getContrastColor

private val defaultPalette = listOf(
    Color(0xFF000000),
    Color(0xFFC83232),
    Color(0xFF4CAF50),
    Color(0xFF2196F3),
    Color(0xFFFF9800),
)

@Composable
fun ColorPicker(
    title: String,
    selectedColor: Color,
    onColorSelected: (Color) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier,
        verticalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        Text(
            text = title,
            style = MaterialTheme.typography.titleSmall,
            fontWeight = FontWeight.Medium,
        )

        Row(
            horizontalArrangement = Arrangement.spacedBy(4.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            defaultPalette.forEach { color ->
                ColorSwatch(
                    color = color,
                    isSelected = color == selectedColor,
                    onClick = { onColorSelected(color) },
                )
            }
            VerticalDivider(modifier = Modifier.width(1.dp).height(32.dp))

            var showCustomColorPicker by remember { mutableStateOf(false) }
            var pencilBounds by remember { mutableIntStateOf(0) }
            Box(
                modifier = Modifier.onGloballyPositioned { coords ->
                    pencilBounds = coords.size.width
                },
            ) {
                var tempColor by remember(selectedColor) { mutableStateOf(selectedColor) }
                LaunchedEffect(showCustomColorPicker) {
                    if (!showCustomColorPicker) {
                        onColorSelected(tempColor)
                    }
                }

                ColorSwatch(
                    color = tempColor,
                    isSelected = tempColor !in defaultPalette,
                    icon = Icons.Pen,
                    onClick = { showCustomColorPicker = true },
                )

                if (showCustomColorPicker) {
                    Popup(
                        alignment = Alignment.TopStart,
                        offset = IntOffset(pencilBounds + with(LocalDensity.current) { 16.dp.roundToPx() }, 0),
                        onDismissRequest = { showCustomColorPicker = false },
                        properties = PopupProperties(focusable = true),
                    ) {
                        Card(
                            shape = RoundedCornerShape(16.dp),
                            colors = CardDefaults.cardColors(
                                containerColor = MaterialTheme.colorScheme.surface,
                            ),
                            modifier = Modifier.width(240.dp),
                        ) {
                            ColorPalette(
                                color = selectedColor,
                                onColorChanged = { newColor ->
                                    tempColor = newColor
                                },
                                modifier = Modifier.padding(12.dp),
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ColorSwatch(
    color: Color,
    isSelected: Boolean,
    onClick: () -> Unit,
    icon: ImageVector? = null,
    modifier: Modifier = Modifier,
) {
    val iconColor = getContrastColor(color)
    Box(
        modifier = modifier
            .size(32.dp)
            .then(
                if (isSelected) {
                    Modifier.border(
                        width = 2.dp,
                        color = MaterialTheme.colorScheme.primary,
                        shape = RoundedCornerShape(8.dp),
                    )
                } else {
                    Modifier
                },
            )
            .clip(RoundedCornerShape(6.dp))
            .clickable(onClick = onClick),
        contentAlignment = Alignment.Center
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(4.dp)
                .clip(RoundedCornerShape(6.dp))
                .background(color),
        )
        if (icon != null) {
            Image(
                modifier = Modifier.size(16.dp),
                imageVector = icon,
                contentDescription = null,
                colorFilter = ColorFilter.tint(iconColor),
            )
        }
    }
}

@Preview(showBackground = true)
@Composable
private fun ColorPickerPreview() {
    var selected by remember { mutableStateOf(Color(0xFFC83232)) }
    Box(modifier = Modifier.padding(16.dp)) {
        ColorPicker(
            title = "Stroke",
            selectedColor = selected,
            onColorSelected = { selected = it },
        )
    }
}
